import { ArrowRight } from "lucide-react";

export default function UpsellSection() {
  return (
    <section
      data-testid="upsell-section"
      className="bg-[#054452] py-16 sm:py-24 px-6"
    >
      <div className="max-w-[680px] mx-auto text-center">
        {/* Eyebrow */}
        <p className="font-body text-xs tracking-[0.25em] uppercase text-[#F3B21A] mb-6 animate-fade-in-up">
          Go Deeper
        </p>

        {/* Headline */}
        <h2
          className="font-display text-3xl sm:text-4xl font-light text-white leading-tight mb-6"
          data-testid="upsell-headline"
        >
          The Identity <em className="text-[#E66E6D]">Audit</em>
        </h2>

        <div className="w-10 h-[1px] bg-[#F3B21A] mx-auto mb-8" />

        {/* Body */}
        <p className="font-body text-base text-[#F8D4CE] font-light max-w-[520px] mx-auto mb-4 leading-relaxed">
          The Analyzer showed you the pattern. The Audit helps you dismantle it.
        </p>
        <p className="font-body text-sm text-[#0C6474] font-light max-w-[480px] mx-auto mb-10 leading-relaxed">
          A comprehensive, AI-powered deep dive into your identity architecture
          — complete with a personalized realignment roadmap built specifically
          for your pattern combination.
        </p>

        {/* What's included */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 text-left max-w-[520px] mx-auto">
          {[
            "Full identity pattern analysis",
            "Root belief mapping",
            "Decision pattern breakdown",
            "Personalized realignment roadmap",
            "Identity distortion reframe",
            "90-day integration plan",
          ].map((item) => (
            <div key={item.replace(/\s/g, '-')} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F3B21A] mt-1.5 flex-shrink-0" />
              <span className="font-body text-sm text-[#F8D4CE] font-light">
                {item}
              </span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <a
          href="https://link.getcmm.com/widget/booking/f7hS6pBZfsINb2FaOZHo"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="upsell-cta-btn"
          className="inline-flex items-center gap-3 bg-[#F3B21A] text-[#054452] w-full max-w-[400px] justify-center py-4 font-body font-medium text-sm tracking-wide uppercase hover:bg-[#E66E6D] hover:text-white transition-colors duration-300"
        >
          Book Your Identity Audit
          <ArrowRight size={16} />
        </a>

        {/* Footer note */}
        <p className="font-body text-xs text-[#0C6474] mt-8 mb-6">
          Limited availability. By appointment only.
        </p>

        {/* Social Links */}
        <div className="flex items-center justify-center gap-5">
          <a href="https://www.instagram.com/nijahalmond" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
          <a href="https://www.facebook.com/nijahalmond" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
          </a>
          <a href="https://www.youtube.com/@nijahalmondspeaks" target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.43z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>
          </a>
          <a href="https://www.tiktok.com/@nijah.almond" target="_blank" rel="noopener noreferrer" data-testid="social-tiktok" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
          </a>
          <a href="https://www.linkedin.com/in/nijahalmond" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
          </a>
          <a href="https://link.getcmm.com/widget/booking/f7hS6pBZfsINb2FaOZHo" target="_blank" rel="noopener noreferrer" data-testid="social-calendar" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </a>
          <a href="https://open.substack.com/pub/nijahalmond" target="_blank" rel="noopener noreferrer" data-testid="social-substack" className="text-[#0C6474] hover:text-[#F3B21A] transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="4" x2="20" y2="4"/><line x1="4" y1="10" x2="20" y2="10"/><path d="M4 16l8 5 8-5"/></svg>
          </a>
        </div>
        <p className="font-body text-[10px] text-[#0C6474] mt-3">@nijahalmond</p>

        {/* Copyright & Legal */}
        <div className="mt-10 pt-6 border-t border-[#0C6474] w-full max-w-[520px] mx-auto">
          <p className="font-body text-[10px] text-[#0C6474] mb-2">
            &copy; {new Date().getFullYear()} Nijah Almond Coaching & Consultancy. All rights reserved.
          </p>
          <p className="font-body text-[9px] text-[#0C6474] leading-relaxed mb-2">
            The Identity Analyzer&trade; and Identity Audit&trade; are proprietary tools of Nijah Almond Coaching & Consultancy. All content, frameworks, archetype definitions, and AI-generated reports are protected intellectual property.
          </p>
          <p className="font-body text-[9px] text-[#0C6474] leading-relaxed">
            This tool is for educational and self-awareness purposes only. It is not a substitute for professional therapy, clinical diagnosis, or medical advice. Results are personalized and confidential.
          </p>
        </div>
      </div>
    </section>
  );
}
